import http from 'apis/config/http-service';
import { useQueryWrapper } from 'apis/config/use-api-wrapper';
import type { AxiosError } from 'axios';
import { useCustomToast } from 'design-system';
import { TParam, usePageParams } from 'hooks/use-page-params';
import { UseQueryOptions, UseQueryResult } from 'react-query';
import { downloadFile } from 'utils/download-file';
import { restaurantInquiriesEndpoint } from './use-create-restaurant-inquiry-api';

// Exports everything matching the current filters (no page/limit), capped at
// 5000 rows server-side. Intentionally flat — no per-quote/per-restaurant columns.
const endpoint = `${restaurantInquiriesEndpoint}/master-products/unmatched-demand/csv`;

type TData = Record<string, any>;
type TError = AxiosError;
type TQueryKey = [typeof endpoint, { status: TParam; category: TParam; origin: TParam; sort: TParam }];
type Options = Omit<UseQueryOptions<unknown, TError, TData, TQueryKey>, 'queryKey' | 'queryFn'>;
type ReturnType = UseQueryResult<TData, TError>;

export function useDownloadUnmatchedDemandCsvApi(options?: Options): ReturnType {
  const toast = useCustomToast();
  const { getPageParams } = usePageParams();
  const { status, category, origin, sort } = getPageParams();

  const queryFn = async () => {
    try {
      const response = await http.get(endpoint, {
        params: { status, category, origin, sort },
        responseType: 'arraybuffer',
      });

      downloadFile(response.data, 'unmatched-master-product-demand.csv');
    } catch (error: any) {
      if (!error.response?.status || !error.response?.data?.message) throw error;

      toast({
        description: error.response.data.message,
        status: 'error',
      });

      throw error;
    }
  };

  return useQueryWrapper<unknown, TError, TData, TQueryKey>([endpoint, { status, category, origin, sort }], queryFn, {
    enabled: false,
    ...options,
  });
}
